import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { OrgDraftPart, Organization, OrgPart } from '@newgenus/common';
import { Subscription } from 'rxjs';

import {
    BuilderConfig,
    BuilderSavePayload,
    BuilderTreeNode,
} from '../../org-board/shared/org-part-tree-builder/builder-models';

import { OrgWizardStateService } from '../services/org-wizard-state.service';

type PartType = OrgDraftPart['partType'];

const DEFAULT_COLOUR = { background: '#1976d2', color: '#ffffff', linkColor: '#0d47a1' };

/**
 * Level-aware allowed-children resolver — mirrors the org-board bulk-create
 * dialog so the wizard's structure step behaves identically to the post-create
 * "add positions" experience users will see later.
 */
export function bulkAllowedChildTypes(parentType: string, level = 0): string[] {
    switch ((parentType || '').toLowerCase()) {
        case 'executive':
            return level <= 1 ? ['executive', 'post'] : ['division', 'post'];
        case 'division':
            return ['department', 'post'];
        case 'department':
            return ['section', 'post'];
        case 'section':
            return ['post', 'unit'];
        case 'unit':
            return ['post'];
        case 'post':
            return [];
        default:
            return ['executive', 'division', 'department', 'section', 'unit', 'post'];
    }
}

/**
 * Step 3 — Structure editor (R9.2 refactor).
 *
 * Replaces the bespoke cdkDropList tree with a thin wrapper around the shared
 * `OrgPartTreeBuilderComponent` (the same widget the org-board edit / bulk-
 * create-position dialogs use). This gives the wizard:
 *   - drag-and-drop with proper cross-parent reparenting,
 *   - per-row colour, type, header-role pickers,
 *   - the level-aware bulk part-type rules,
 *   - the same dirty / move / delete affordances users already know from the
 *     post-create org-board.
 *
 * Wizard-specific tweaks via `BuilderConfig`:
 *   - `mode: 'bulk'` + `protectedRootPartKey: undefined` — the wizard has no
 *     immutable CEO root yet, every node is freely draggable;
 *   - `maxPositions` mirrors `OrgWizardStateService.maxParts` (defaults to
 *     1000, override-able via `systemConfig/orgWizardLimits`);
 *   - `skipTaskMonitor: true` — saves are an in-memory mutation of
 *     `OrgDraft.structure`; there are no `securityTasks/` rows to wait on;
 *   - synthetic `Organization` shell fed from the live draft (the builder
 *     reads `organization.orgParts` for breadcrumb and task-name resolution).
 */
@Component({
    selector: 'newgenus-org-creation-step-structure',
    templateUrl: './step-3-structure.component.html',
    styleUrls: ['./step-3-structure.component.scss'],
    standalone: false,
})
export class StepStructureComponent implements OnInit, OnDestroy {
    public state = inject(OrgWizardStateService);

    public get maxParts(): number {
        return this.state.maxParts;
    }
    public get maxDepth(): number {
        return this.state.maxDepth;
    }
    public readonly partTypes: PartType[] = ['executive', 'division', 'department', 'section', 'unit', 'post'];

    /** Inputs handed to the shared builder. Rebuilt every time the draft
     *  structure changes from outside (load, undo/redo). The builder's
     *  ngOnChanges adopts the new tree automatically when the reference
     *  changes, so no manual re-instantiation is needed. */
    public initialTree: BuilderTreeNode[] = [];
    public config: BuilderConfig | null = null;

    private sub = new Subscription();

    public ngOnInit(): void {
        // Auto-pick the CEO post on entry so well-formed drafts (Excel, scaffold,
        // template) need zero clicks. Idempotent — no-op when something is
        // already crowned. Plan addendum 19.
        this.state.autoPickCeoPostIfNeeded();
        this.refreshFromDraft();
        this.sub.add(
            this.state.change$.subscribe(({ source, ui }) => {
                if (source === 'load' || source === 'undo' || source === 'redo') {
                    // Re-pick the crown after a load/undo/redo if needed —
                    // a delete-of-crowned undo could leave the structure with
                    // no crown, and we want the panel to always reflect a
                    // valid CEO. autoPickCeoPostIfNeeded skips when one
                    // already exists, so undo of a manual re-pick stays
                    // honoured.
                    this.state.autoPickCeoPostIfNeeded();
                    this.refreshFromDraft(ui);
                }
            })
        );
    }

    // ─── CEO post selector (crown 👑) — addendum 19 ──────────────────────
    /** Currently-crowned partKey, proxied from the wizard state service. */
    public get ceoPostKey(): string | null {
        return this.state.ceoPostKey;
    }

    /** Display name of the currently-crowned post for the hint strip. */
    public get ceoPostName(): string {
        const draft = this.state.snapshot;
        const key = this.state.ceoPostKey;
        if (!draft || !key) return '';
        const p = draft.structure.find((x) => x.partKey === key);
        return p?.name?.trim() || '(unnamed)';
    }

    /** BuilderConfig hook — true when the supplied post carries the crown. */
    private isPostCrowned = (partKey: string): boolean => this.state.ceoPostKey === partKey;

    /** BuilderConfig hook — clicking the crown on a post row moves the
     *  crown there. Idempotent: re-clicking the already-crowned post is a
     *  no-op (state.setCeoPost guards against same-key dispatches). */
    private togglePostCrown = (partKey: string): void => {
        this.state.setCeoPost(partKey);
    };

    public ngOnDestroy(): void {
        this.sub.unsubscribe();
    }

    private refreshFromDraft(ui?: { structureTree?: unknown } | null): void {
        const draft = this.state.snapshot;
        if (!draft) {
            this.initialTree = [];
            this.config = null;
            return;
        }
        const snapshotTree = ui?.structureTree as BuilderTreeNode[] | undefined;
        // Prefer the captured BuilderTreeNode[] snapshot (preserves per-node
        // dirty/moved/isNew/_original markers + colour swatches). Fall back to
        // rebuilding from the flattened OrgDraftPart[] when no snapshot exists
        // (initial load, or a draft loaded from Firestore that never had a
        // builder snapshot stashed).
        this.initialTree = snapshotTree ?? this.draftPartsToBuilderTree(draft.structure);
        this.config = this.buildConfig();
    }

    // ─────────────────────────────────────────────────────────────────────
    // Adapters: OrgDraftPart[] ↔ BuilderTreeNode[]
    // ─────────────────────────────────────────────────────────────────────

    private draftPartsToBuilderTree(parts: OrgDraftPart[]): BuilderTreeNode[] {
        const byParent = new Map<string, OrgDraftPart[]>();
        for (const p of parts) {
            const list = byParent.get(p.parentPartKey) ?? [];
            list.push(p);
            byParent.set(p.parentPartKey, list);
        }
        for (const list of byParent.values()) list.sort((a, b) => a.id - b.id);

        const build = (parent: string, level: number): BuilderTreeNode[] =>
            (byParent.get(parent) ?? []).map((p, idx) =>
                this.draftPartToBuilderNode(p, idx, level, build(p.partKey, level + 1))
            );

        return build('', 0);
    }

    private draftPartToBuilderNode(
        p: OrgDraftPart,
        index: number,
        level: number,
        children: BuilderTreeNode[]
    ): BuilderTreeNode {
        const colour = this.colourFromPart(p);
        const headerRole = (p.headerRole ?? '') as 'head' | 'deputy' | 'assistant' | '';
        return {
            // Treat every wizard node as "existing" (isNew=false) so the
            // builder shows the full per-row controls instead of the new-node
            // lite UI. The wizard never round-trips to the bulkSave backend, so
            // the isNew/_originalParentPartKey distinction has no functional
            // effect here.
            partKey: p.partKey,
            isNew: false,
            name: p.name,
            subTitle: '',
            partType: p.partType,
            colour,
            vfp: p.vfp ?? '',
            headerRole,
            children,
            parentPartKey: p.parentPartKey,
            _originalParentPartKey: p.parentPartKey,
            _originalIndex: index,
            depth: p.depth,
            id: p.id,
            collapsed: false,
            level,
            dirty: false,
            moved: false,
            _original: {
                name: p.name,
                subTitle: '',
                partType: p.partType,
                colour: { ...colour },
                vfp: p.vfp ?? '',
                headerRole,
            },
        };
    }

    private colourFromPart(p: OrgDraftPart): { background: string; color: string; linkColor: string } {
        const c = (p as { colour?: Partial<typeof DEFAULT_COLOUR> }).colour;
        return {
            background: c?.background || DEFAULT_COLOUR.background,
            color: c?.color || DEFAULT_COLOUR.color,
            linkColor: c?.linkColor || DEFAULT_COLOUR.linkColor,
        };
    }

    /**
     * Flatten the builder's working tree back into `OrgDraftPart[]`, computing
     * fresh `parentPartKey`, `id`, and `depth` from the tree topology.
     */
    private builderTreeToDraftParts(tree: readonly BuilderTreeNode[]): OrgDraftPart[] {
        // Snapshot the current draft's isCeoPost flags by partKey so the round-
        // trip through the shared builder (which has no concept of isCeoPost)
        // doesn't drop the crown. Plan addendum 19.
        const prevDraft = this.state.snapshot;
        const wasCrownedByKey = new Set<string>(
            (prevDraft?.structure ?? [])
                .filter((p) => (p as { isCeoPost?: boolean }).isCeoPost === true)
                .map((p) => p.partKey)
        );
        const out: OrgDraftPart[] = [];
        const walk = (siblings: readonly BuilderTreeNode[], parentKey: string, depth: number): void => {
            siblings.forEach((node, idx) => {
                const partType = (node.partType as PartType) || 'post';
                const part: OrgDraftPart = {
                    partKey: node.partKey,
                    name: (node.name || 'Unnamed').trim(),
                    partType,
                    parentPartKey: parentKey,
                    id: idx,
                    depth,
                };
                if (node.vfp) part.vfp = node.vfp;
                if (node.headerRole && partType === 'post') {
                    part.headerRole = node.headerRole as 'head' | 'deputy' | 'assistant';
                }
                // Re-stamp the crown ONLY if the part is still a post —
                // demoting a crowned post to e.g. a unit silently drops the
                // crown and the auto-pick after save will re-anchor it.
                if (partType === 'post' && wasCrownedByKey.has(node.partKey)) {
                    (part as { isCeoPost?: boolean }).isCeoPost = true;
                }
                // Preserve colour as a passthrough — OrgDraftPart's `[extra]`
                // index signature accepts it and `createFromDraft` forwards it
                // straight into the live OrgPart.
                (part as { colour?: typeof node.colour }).colour = { ...node.colour };
                out.push(part);
                walk(node.children, node.partKey, depth + 1);
            });
        };
        walk(tree, '', 0);
        return out;
    }

    // ─────────────────────────────────────────────────────────────────────
    // BuilderConfig
    // ─────────────────────────────────────────────────────────────────────

    private buildConfig(): BuilderConfig {
        const draft = this.state.snapshot;
        const orgName = draft?.basics.name?.trim() || 'New Organization';
        return {
            mode: 'bulk',
            minDepth: 0,
            protectedRootPartKey: undefined,
            showAddRootButton: true,
            maxPositions: this.state.maxParts,
            getAllowedChildTypes: bulkAllowedChildTypes,
            defaultColour: { ...DEFAULT_COLOUR },
            hideSubtitleForNewNodes: false,
            anchorPartKey: '',
            insertionType: 'child',
            breadcrumb: [{ name: orgName, partType: 'organization' }],
            showInsertionPointChip: false,
            // Intentionally blank — the wizard step header above the builder
            // already shows the lede ("Drag, rename, and reparent positions…")
            // so we keep the builder header tight to leave room for the
            // change-count pills + Add Root + field-toggle chips at narrower
            // widths (otherwise they wrap into the subtitle).
            headerSubtitle: '',
            headerIcon: 'account_tree',
            headerTitle: 'Organization structure',
            saveButtonLabel: 'Save structure',
            showDeveloperInfo: false,
            organization: this.synthesiseOrganization(draft?.structure ?? []),
            preSave: undefined,
            saveCommand: (payload, tree) => this.saveCommand(payload, tree),
            // No refreshTree — the wizard's data lives entirely in memory and
            // there's nothing to re-fetch. Continue Editing in the builder
            // simply re-uses the freshly-saved tree.
            refreshTree: undefined,
            skipTaskMonitor: true,
            // AutoSave mode: every mutation flushes back into the wizard's
            // OrgWizardStateService → each becomes a wizard undo entry, and
            // the wizard's top-bar Undo / Redo govern reverting structure
            // edits. Hides the builder's own footer + Pending Changes panel.
            autoSave: true,
            // Row-level CEO crown — addendum 19. The shared builder renders
            // a crown button on every post row that proxies into wizard
            // state via these hooks; the selection lives outside the
            // builder's own dirty/move/delete bookkeeping.
            isPostCrowned: this.isPostCrowned,
            onTogglePostCrown: this.togglePostCrown,
        };
    }

    /**
     * Synthetic Organization shell so the builder's task-monitor and breadcrumb
     * code (which reads `organization.orgParts`) doesn't crash. Only fields
     * actually consumed by the builder are populated.
     */
    private synthesiseOrganization(parts: OrgDraftPart[]): Organization {
        const orgParts: OrgPart[] = parts.map(
            (p) =>
                ({
                    partKey: p.partKey,
                    name: p.name,
                    subTitle: '',
                    partType: p.partType,
                    parentPartKey: p.parentPartKey,
                    id: p.id,
                    depth: p.depth,
                    path: '',
                    colour: this.colourFromPart(p),
                    vfp: p.vfp,
                    headerRole: p.headerRole,
                }) as OrgPart
        );
        return { key: 'draft', orgParts } as unknown as Organization;
    }

    // ─────────────────────────────────────────────────────────────────────
    // Save handler — builder tree → OrgDraftPart[] → wizard state
    // ─────────────────────────────────────────────────────────────────────

    private async saveCommand(_payload: BuilderSavePayload, tree?: readonly BuilderTreeNode[]): Promise<void> {
        if (!tree) return;
        const flattened = this.builderTreeToDraftParts(tree);
        // Push BOTH the flattened OrgDraftPart[] AND the live BuilderTreeNode
        // snapshot into wizard state. Each save becomes one undo entry that,
        // when reverted, restores the flattened parts (for the rest of the
        // wizard) AND the per-node dirty/moved/isNew/_original markers +
        // colour swatches (for the builder UI in Step 3).
        this.state.setStructure(flattened, { ui: { structureTree: tree as BuilderTreeNode[] } });
        // Re-evaluate the crown: if the previously-crowned post was deleted
        // or demoted to a non-post type, auto-pick the next lowest-depth post.
        // Plan addendum 19.
        this.state.autoPickCeoPostIfNeeded();
    }
}
